<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main
      v-else
      class="split-gallery"
      tabindex="0"
      @pointerdown="handlePointerDown"
      @pointerup="handlePointerUp"
      @pointercancel="resetPointer"
    >
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans la galerie</div>

      <template v-else>
        <section class="copy-panel">
          <h1>{{ exhibitName }}</h1>
          <p class="description">{{ activeResource.description || activeResource.title || 'Explorez les ressources de cette exposition.' }}</p>

          <div class="resource-meta">
            <span>{{ activeIndex + 1 }} / {{ resources.length }}</span>
            <span>{{ activeResource.title || 'Ressource' }}</span>
          </div>

          <div class="controls" aria-label="Navigation galerie">
            <button type="button" @click="previous" aria-label="Ressource précédente">
              <q-icon name="chevron_left" />
            </button>
            <button type="button" @click="next" aria-label="Ressource suivante">
              <q-icon name="chevron_right" />
            </button>
          </div>
        </section>

        <section class="media-panel">
          <div class="media-card" @click="lightboxOpen = true">
            <img
              v-if="isImageResource(activeResource) && activeUrl"
              :key="activeUrl"
              :src="activeUrl"
              :alt="activeResource.title || exhibitName"
            />
            <video v-else-if="getResourceType(activeResource) === 'video' && activeUrl" :src="activeUrl" controls playsinline />
            <div v-else class="file-card">
              <q-icon :name="fileIcon(activeResource)" />
              <span>{{ activeUrl === null ? 'Media indisponible' : activeResource.title || activeResource.name || 'Chargement...' }}</span>
            </div>
          </div>

          <div class="dots" aria-label="Choisir une ressource">
            <button
              v-for="(_, index) in resources"
              :key="index"
              type="button"
              :class="{ active: index === activeIndex }"
              @click="goTo(index)"
              :aria-label="`Ressource ${index + 1}`"
            ></button>
          </div>
        </section>
      </template>
    </main>

    <transition name="fade">
      <div v-if="lightboxOpen" class="lightbox" @click="lightboxOpen = false">
        <div class="lightbox-content" @click.stop>
          <button class="close-button" type="button" @click="lightboxOpen = false" aria-label="Fermer">
            <q-icon name="close" />
          </button>
          <img v-if="isImageResource(activeResource) && activeUrl" :src="activeUrl" :alt="activeResource.title || exhibitName" />
          <video v-else-if="getResourceType(activeResource) === 'video' && activeUrl" :src="activeUrl" controls autoplay playsinline />
          <div v-else class="file-card file-card--large">
            <q-icon :name="fileIcon(activeResource)" />
            <span>{{ activeUrl === null ? 'Media indisponible' : activeResource.title || activeResource.name || 'Chargement...' }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  getResourceType,
  homeImage,
  homePage,
  id,
  isImageResource,
  loading,
  resourceUrls,
  resources,
} = useGalleryTemplate(props);

const activeIndex = ref(0);
const lightboxOpen = ref(false);
const pointerStartX = ref(null);

const activeResource = computed(() => resources.value[activeIndex.value] || {});
const activeUrl = computed(() => resourceUrls.value[activeIndex.value] || '');

const goTo = (index) => {
  if (!resources.value.length) return;
  activeIndex.value = (index + resources.value.length) % resources.value.length;
};
const next = () => goTo(activeIndex.value + 1);
const previous = () => goTo(activeIndex.value - 1);

const handlePointerDown = (event) => {
  pointerStartX.value = event.clientX;
};

const handlePointerUp = (event) => {
  if (pointerStartX.value === null) return;
  const delta = event.clientX - pointerStartX.value;
  if (Math.abs(delta) > 48) {
    delta < 0 ? next() : previous();
  }
  resetPointer();
};

const resetPointer = () => {
  pointerStartX.value = null;
};

const fileIcon = (resource) => {
  const type = getResourceType(resource);
  if (type === 'audio') return 'audiotrack';
  if (type === 'pdf' || type === 'doc') return 'description';
  return 'insert_drive_file';
};
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.split-gallery {
  display: grid;
  grid-template-columns: minmax(280px, 0.78fr) minmax(360px, 1.2fr);
  gap: 56px;
  min-height: 100vh;
  padding: 64px 72px;
  align-items: center;
  touch-action: pan-y;
}

.copy-panel {
  max-width: 520px;
  justify-self: center;
  animation: copyIn 520ms ease both;
}

h1 {
  margin: 0 0 28px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 86px;
  font-weight: 400;
  line-height: 0.95;
}

.description {
  max-width: 360px;
  margin: 0;
  color: #f4f4f4;
  font-size: 28px;
  line-height: 1.35;
}

.resource-meta {
  display: flex;
  gap: 14px;
  margin-top: 34px;
  color: #b8b8b8;
  font-size: 14px;
}

.controls {
  display: flex;
  gap: 12px;
  margin-top: 30px;
}

.controls button,
.dots button,
.close-button {
  border: 0;
  cursor: pointer;
}

.controls button {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 26px;
  transition: transform 160ms ease, background 160ms ease;
}

.controls button:active {
  transform: scale(0.94);
}

.controls button:hover {
  background: rgba(255, 255, 255, 0.24);
}

.media-panel {
  display: grid;
  gap: 22px;
  justify-items: center;
}

.media-card {
  width: min(100%, 690px);
  aspect-ratio: 4 / 5;
  max-height: 78vh;
  overflow: hidden;
  background: #171717;
  cursor: pointer;
  animation: imageIn 600ms ease both;
}

.media-card img,
.media-card video,
.lightbox-content img,
.lightbox-content video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-card:hover img {
  transform: scale(1.025);
}

.media-card img {
  transition: transform 420ms ease;
}

.dots {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.dots button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.42);
  transition: transform 160ms ease, background 160ms ease;
}

.dots button.active {
  background: #fff;
  transform: scale(1.35);
}

.state-panel {
  grid-column: 1 / -1;
  text-align: center;
  color: #ddd;
}

.file-card {
  display: grid;
  place-items: center;
  gap: 14px;
  height: 100%;
  padding: 32px;
  color: #fff;
  text-align: center;
}

.file-card .q-icon {
  font-size: 62px;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.88);
}

.lightbox-content {
  position: relative;
  width: min(1120px, 94vw);
  height: min(820px, 88vh);
}

.lightbox-content img,
.lightbox-content video {
  object-fit: contain;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  color: #000;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes copyIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes imageIn {
  from {
    opacity: 0;
    transform: translateX(18px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@media (max-width: 980px) {
  .split-gallery {
    grid-template-columns: 1fr;
    gap: 34px;
    padding: 44px 28px;
  }

  .copy-panel {
    max-width: none;
    justify-self: stretch;
  }

  h1 {
    font-size: 58px;
  }

  .description {
    max-width: none;
    font-size: 22px;
  }

  .media-card {
    width: 100%;
    aspect-ratio: 16 / 11;
  }
}

@media (max-width: 620px) {
  .split-gallery {
    padding: 30px 18px;
  }

  h1 {
    font-size: 46px;
  }

  .description {
    font-size: 18px;
  }
}
</style>
